/**
 * Unified Career Database for PathFinder
 * Combines O*NET careers (637) with manually curated expanded careers
 * 
 * Total: 650+ careers covering all major no-degree-required occupations
 */

import { ONET_CAREERS } from './onetCareers';
import { EXPANDED_CAREERS } from './expandedCareers';
import { EXPANDED_CAREERS_V2 } from './expandedCareersDatabase';
import { CareerPath } from '../careerPaths';
import { ExpandedCareer } from '../types/expandedCareerTypes';

/**
 * Convert ExpandedCareer (v2 format) to CareerPath (v1 format) for compatibility
 */
function expandedToCareerPath(career: ExpandedCareer): CareerPath {
  return {
    id: career.id,
    title: career.title,
    category: career.category,
    keywords: career.keywords,
    salary: {
      entry_min: career.salary.entry_min,
      entry_max: career.salary.entry_max,
    },
    description: career.description,
    dailyActivities: career.dailyActivities,
    requiredSkills: career.requiredSkills,
    niceToHave: career.niceToHave,
    commonTransitionsFrom: career.commonTransitionsFrom,
  };
}

/**
 * All O*NET careers converted to CareerPath format
 * 637 careers from the U.S. Department of Labor O*NET database
 */
export const ONET_CAREER_PATHS: CareerPath[] = ONET_CAREERS.map(expandedToCareerPath);

/**
 * Manually curated careers with rich pathway data (v2 format)
 * These have additional fields like military MOS mappings, certifications, etc.
 */
export const CURATED_CAREERS: ExpandedCareer[] = EXPANDED_CAREERS_V2;

/**
 * Combined career database - all careers in CareerPath format
 * Use this for the main matching algorithm
 */
export const ALL_CAREER_PATHS: CareerPath[] = [
  ...EXPANDED_CAREERS,  // 50 manually curated careers (v1 format)
  ...ONET_CAREER_PATHS, // 637 O*NET careers
].filter((career, index, self) => 
  // Remove duplicates by ID
  index === self.findIndex(c => c.id === career.id)
);

/**
 * Combined career database - all careers in ExpandedCareer format
 * Use this for advanced features (pathways, military translation, etc.)
 */
export const ALL_EXPANDED_CAREERS: ExpandedCareer[] = [
  ...EXPANDED_CAREERS_V2, // 18 manually curated with rich data
  ...ONET_CAREERS,        // 637 O*NET careers
].filter((career, index, self) => 
  // Remove duplicates by ID
  index === self.findIndex(c => c.id === career.id)
);

/**
 * Get career counts by category
 */
export function getCareerCountsByCategory(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const career of ALL_CAREER_PATHS) {
    counts[career.category] = (counts[career.category] || 0) + 1;
  }
  return counts;
}

/**
 * Get all unique categories
 */
export function getAllCategories(): string[] {
  const categories = new Set(ALL_CAREER_PATHS.map(c => c.category));
  return Array.from(categories).sort();
}

/**
 * Search careers by keyword or title
 */
export function searchCareers(query: string, limit = 20): CareerPath[] {
  const queryLower = query.toLowerCase();
  
  return ALL_CAREER_PATHS
    .filter(career => 
      career.title.toLowerCase().includes(queryLower) ||
      career.description.toLowerCase().includes(queryLower) ||
      career.keywords.some(k => k.toLowerCase().includes(queryLower)) ||
      career.category.toLowerCase().includes(queryLower)
    )
    .slice(0, limit);
}

/**
 * Get careers by category
 */
export function getCareersByCategory(category: string): CareerPath[] {
  return ALL_CAREER_PATHS.filter(c => 
    c.category.toLowerCase() === category.toLowerCase()
  );
}

// Export counts for reference
export const CAREER_COUNTS = {
  onet: ONET_CAREERS.length,
  curated_v1: EXPANDED_CAREERS.length,
  curated_v2: EXPANDED_CAREERS_V2.length,
  total: ALL_CAREER_PATHS.length,
};

export default ALL_CAREER_PATHS;

// Legacy exports for backward compatibility
export const allCareers = ALL_CAREER_PATHS;
